import type { ReactNode } from 'react';
import { localZipRecentDisplayPath, type LocalZipRecentEntry } from '../../../utils/localZip/localZipRecentStore';
import styles from './LocalZipFileIdentity.module.css';

export type LocalZipFileIdentityProps = {
  name: string;
  /** OS パス等。name と同じ／空ならパス行は出さない */
  path?: string | null;
  className?: string;
  /** 追加の補足（例: 「続きから開く」） */
  hint?: ReactNode;
};

/**
 * ローカル Zip／ローカル正本ツール共通のファイル識別表示。
 * ファイル名を主、パスがあれば副行（monospace・末尾優先省略）。
 */
export function LocalZipFileIdentity({
  name,
  path,
  className = '',
  hint,
}: LocalZipFileIdentityProps) {
  const trimmedName = name.trim() || '—';
  const trimmedPath = path?.trim() && path.trim() !== trimmedName ? path.trim() : null;
  const title = trimmedPath || trimmedName;

  return (
    <span className={`${styles.root}${className ? ` ${className}` : ''}`.trim()} title={title}>
      <span className={styles.name}>{trimmedName}</span>
      {trimmedPath ? <span className={styles.path}>{trimmedPath}</span> : null}
      {hint ? <span className={styles.hint}>{hint}</span> : null}
    </span>
  );
}

export type LocalZipRecentListProps = {
  entries: LocalZipRecentEntry[];
  className?: string;
  emptyLabel?: ReactNode;
  /** 先頭（最新）以外も列挙するときの見出し */
  heading?: ReactNode;
};

/**
 * 過去に使ったローカル Zip の一覧（パス付き）。ゲート周辺で流用。
 */
export function LocalZipRecentList({
  entries,
  className = '',
  emptyLabel,
  heading,
}: LocalZipRecentListProps) {
  if (entries.length === 0) {
    return emptyLabel ? (
      <div className={`${styles.list}${className ? ` ${className}` : ''}`.trim()}>{emptyLabel}</div>
    ) : null;
  }

  return (
    <div className={`${styles.list}${className ? ` ${className}` : ''}`.trim()}>
      {heading ? <div className={styles.listHeading}>{heading}</div> : null}
      <ul className={styles.listItems}>
        {entries.map((entry) => {
          const key = `${entry.path || entry.name}|${entry.updatedAt}`;
          return (
            <li key={key} className={styles.listItem}>
              <LocalZipFileIdentity name={entry.name} path={entry.path} />
              <span className={styles.listMeta} title={localZipRecentDisplayPath(entry)}>
                {formatRecentWhen(entry.updatedAt)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatRecentWhen(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()) || d.getTime() === 0) return '';
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
